import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CloseMethod } from '@chat-emp/shared';

import { LiveDataBridge } from '../data/LiveDataBridge.js';
import { useHostProps } from '../hooks/useHostProps.js';
import { useHostUnread } from '../hooks/useHostUnread.js';
import { useUnreadCount } from '../hooks/useUnreadCount.js';
import { useStartChatRequest } from '../hooks/useStartChatRequest.js';
import { track } from '../lib/analytics.js';
import { emitToHost, onBridgeMessage } from '../lib/bridge.js';
import { startReplay } from '../lib/replay.js';
import { useSession } from '../session/SessionProvider.js';
import { StreamProvider } from '../session/StreamProvider.js';
import { DraftThread } from '../widget/DraftThread.js';
import { WidgetPanel } from '../widget/WidgetPanel.js';
import { WidgetView } from './WidgetView.js';

/**
 * Single-chat pane, served at /pane for hosts that open one conversation per row
 * (candidate search). A failed session gets the floating widget here instead;
 * `mode` tells widget.js which of the two to lay out.
 */
export function PaneView() {
  // Mounted before the session resolves: `set-viewport` arrives once, on `ready`.
  useHostProps();
  const { sessionReady, status } = useSession();
  // Decided once per load; a failed session falls back to the widget and its AuthGate.
  const [pane, setPane] = useState<boolean | null>(null);

  useEffect(() => {
    if (pane !== null || (!sessionReady && status === 'idle')) return;
    setPane(sessionReady);
  }, [pane, sessionReady, status]);

  // After the child's effects, so its open-chat listener is up before a host answers `mode`.
  useEffect(() => {
    if (pane !== null) emitToHost('mode', { pane });
  }, [pane]);

  if (pane === null) return null;
  return pane ? <SingleChat /> : <WidgetView />;
}

/** No launcher and no list: only what `openChat` names. Stream connects only while open. */
function SingleChat() {
  const { analyticsId } = useSession();
  const { request, clear } = useStartChatRequest();
  const [open, setOpen] = useState(false);
  // A target has no chat until one is found or sent; until then, no Stream connection.
  const [opened, setOpened] = useState<{ requestId: number; channelId: string } | null>(null);
  const chat = opened && opened.requestId === request?.requestId ? opened : null;
  // The host's navbar badge; the pane has no list to count from, so it polls the BFF.
  useHostUnread(useUnreadCount(true));

  useLayoutEffect(() => {
    const root = document.documentElement.dataset;
    root.surface = 'widget';
    root.pane = '';
    return () => {
      delete root.surface;
      delete root.pane;
    };
  }, []);

  useEffect(() => {
    if (request) setOpen(true);
  }, [request?.requestId]);

  const closedBy = useRef<CloseMethod | 'backdrop' | null>(null);
  const close = (method: CloseMethod | 'backdrop') => {
    closedBy.current = method;
    setOpen(false);
    clear();
  };
  useEffect(
    () =>
      onBridgeMessage((env) => {
        if (env.type === 'close-pane') close('backdrop');
      }),
    [],
  );

  const wasOpen = useRef(false);
  useEffect(() => {
    emitToHost(open ? 'open' : 'close', {});
    if (open) {
      wasOpen.current = true;
      track('pane_open');
    } else if (wasOpen.current) {
      track('pane_close', { method: closedBy.current });
    }
  }, [open]);

  // On opening, never on mount: the host mounts a pane per candidate row. See `lib/replay.ts`.
  useEffect(() => {
    if (open && analyticsId) void startReplay(analyticsId);
  }, [open, analyticsId]);

  if (!open) return null;
  if (request?.target && !chat) {
    return (
      <DraftThread
        key={request.requestId}
        requestId={request.requestId}
        target={request.target}
        draft={request.draft}
        onOpen={(channelId) => setOpened({ requestId: request.requestId, channelId })}
        onClose={close}
      />
    );
  }
  return (
    <StreamProvider connect>
      <LiveDataBridge />
      <WidgetPanel
        pane
        gate
        request={chat ?? request}
        onComposeDone={clear}
        onClose={close}
        onExpand={(req) => emitToHost('expand', req)}
      />
    </StreamProvider>
  );
}
